import React from 'react';
import { useSeo } from '../hooks/useSeo';
import { FinalContact } from '../components/FinalContact';
const GROUPS = [{
  category: 'Getting Started',
  items: [{
    q: 'Who is FSG Transport for?',
    a: 'Truck drivers, owner-operators, small fleets, and carriers looking for dispatch, load planning, broker setup, paperwork, billing, lease-on options, and back-office support.'
  }, {
    q: 'How do I start with FSG?',
    a: 'Choose your setup — own authority, lease on, or not sure — then sign up or call 234-602-8424.'
  }, {
    q: 'What information does FSG need from me?',
    a: 'Company details, MC/DOT (if applicable), equipment type, and required documents to start setup.'
  }, {
    q: 'Can I call instead of filling out a form?',
    a: 'Yes. Call 234-602-8424 to talk to a team member directly.'
  }, {
    q: 'When can I start running?',
    a: 'Timing depends on your setup, required documents, broker approvals, and whether you have your own authority or are applying to lease on.'
  }]
}, {
  category: 'Dispatch Services',
  items: [{
    q: 'Do you force dispatch?',
    a: 'No. FSG presents load options and you choose which loads you accept.'
  }, {
    q: 'Are your dispatchers U.S.-based?',
    a: 'Yes. FSG uses a U.S.-based team with no foreign call centers, and dispatchers speak fluent English and Trucker English.'
  }, {
    q: 'What does FSG handle besides booking loads?',
    a: 'Load planning, broker communication, rate cons, paperwork, billing support, check calls, and follow-up.'
  }, {
    q: 'Do you help with check calls and broker updates?',
    a: 'Yes. FSG handles broker updates and check calls so drivers are not buried in phone calls.'
  }, {
    q: 'Do you help with detention or lumper requests?',
    a: 'Yes. FSG helps communicate with brokers regarding detention and lumper requests.'
  }, {
    q: 'How do you find loads?',
    a: 'FSG uses available freight sources, broker communication, and lane planning to find options that fit your truck. Specific freight sources can vary.'
  }]
}, {
  category: 'Broker Setup & Security',
  items: [{
    q: 'Can you help with broker setup and verification?',
    a: 'Yes. FSG helps with broker packets, clearing house setup, portals, verification steps, and paperwork.'
  }, {
    q: 'What are broker clearing houses?',
    a: 'Systems brokers use to verify carrier identity, safety records, and compliance before awarding loads.'
  }, {
    q: 'Why is broker verification harder now?',
    a: 'Carrier identity theft and fraud checks are making setup harder. FSG helps carriers look organized and verifiable.'
  }]
}, {
  category: 'Paperwork, Billing & Back Office',
  items: [{
    q: 'Can FSG help with rate cons?',
    a: 'Yes. FSG helps keep rate cons organized and ready for billing.'
  }, {
    q: 'Can FSG help keep BOLs and load documents organized?',
    a: 'Yes. FSG helps keep BOLs and billing documents organized so money does not get stuck.'
  }, {
    q: 'How is fuel surcharge handled?',
    a: 'Fuel surcharge handling depends on the load, broker, lane, and agreement. FSG can explain how it is handled during the dispatch setup conversation.'
  }]
}, {
  category: 'Lease-On & Authority',
  items: [{
    q: 'Do I need my own authority?',
    a: 'No. You can run on your own authority or apply to lease on with FSG. Both paths are available.'
  }, {
    q: 'What if I am not sure whether to run under my own authority or lease on?',
    a: 'Talk to FSG. We can explain the differences and help you decide which path fits your goals.'
  }, {
    q: 'How do I apply to lease on?',
    a: 'You can apply online through our IntelliApp link on the Lease On page.'
  }, {
    q: 'What about insurance, registration, and compliance?',
    a: 'FSG can help with documentation, setup, and compliance-related coordination. Insurance, registration, and legal/compliance decisions may require the appropriate provider or authority.'
  }]
}, {
  category: 'Freight, Lanes & Equipment',
  items: [{
    q: 'What equipment do you support?',
    a: 'We support common equipment types like dry van, reefer, flatbed, step deck, and power-only. Freight options depend on your equipment, location, lane, availability, and current market conditions.'
  }, {
    q: 'What lanes do you run?',
    a: 'FSG looks for lanes that make sense for your equipment and goals. The exact lanes depend on freight availability, your setup, and where the truck is running.'
  }]
}, {
  category: 'Pricing & Expectations',
  items: [{
    q: 'What does FSG charge?',
    a: 'Pricing depends on your setup and the support you need. The best next step is to talk with FSG about your situation so the right path is clear.'
  }, {
    q: 'Can you guarantee better loads?',
    a: 'No dispatch company should promise the same result for every truck. Freight options depend on equipment, location, lane, availability, market conditions, broker approval, and carrier setup. FSG focuses on better planning, communication, paperwork, and broker follow-through.'
  }]
}];
export function FaqPage() {
  useSeo({
    title: 'FSG Transport FAQ | Dispatch, Lease-On, Broker Setup & Paperwork',
    description: 'Answers to common questions about FSG Transport dispatch services, leasing on, broker setup and security, paperwork, billing, equipment, lanes, and getting started.',
    path: '/faq'
  });
  return <div className="w-full" data-id="element-468">
      <section className="bg-navy text-white pt-36 pb-16 lg:pt-44 lg:pb-20" data-id="element-469">
        <div className="container-custom max-w-3xl" data-id="element-470">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.1] tracking-tight font-['Barlow']" data-id="element-471">
            Questions Carriers Ask First
          </h1>
        </div>
      </section>

      <section className="section-padding bg-off-white" data-id="element-472">
        <div className="container-custom max-w-4xl space-y-12" data-id="element-473">
          {GROUPS.map((group, i) => <div key={i} data-id="element-474">
              <h2 className="text-xl font-black text-navy uppercase tracking-wide mb-5 pb-3 border-b-2 border-red font-['Barlow']" data-id="element-475">
                {group.category}
              </h2>
              <div className="space-y-5" data-id="element-476">
                {group.items.map((item, j) => <div key={j} className="bg-white border border-steel rounded-sm p-6" data-id="element-477">
                    <h3 className="font-bold text-navy mb-2" data-id="element-478">{item.q}</h3>
                    <p className="text-body font-medium leading-relaxed" data-id="element-479">
                      {item.a}
                    </p>
                  </div>)}
              </div>
            </div>)}
        </div>
      </section>

      <FinalContact data-id="element-480" />
    </div>;
}